import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { TypedMemo } from '../common/types';
import { useShowToast, useToastAction, useToasts } from '../../redux/slices/toasts/hooks';
import { closeToast } from '../../redux/slices/toasts/slice';
import { ToastType } from '../../redux/slices/toasts/state';
import { isNativeEmbedPage } from '../app/isNativeEmbed';
import { appUpdateMessages } from './appUpdateMessages';
import { publishNewBuild, useUpdatePromptOwner } from './appUpdateStore';
import { useVersionPoll } from './useVersionPoll';

/** The id of the "Refresh" action of the update toast. */
export const APP_UPDATE_TOAST_ACTION = 'appUpdate.refresh';
/** The `uniqueId` of the update toast, so it never shows two times at once. */
export const APP_UPDATE_TOAST_ID = 'appUpdate';

interface AppUpdateNoticeProps {
  /** Tests give their own fetch. */
  fetchImpl?: typeof fetch;
  /** Tests replace the reload. */
  reload?: () => void;
}

const reloadPage = () => window.location.reload();

/**
 * Shows "A new version of Music24 is ready" one time for each page load when a new web build is
 * live (the FYM Web update toast). "Refresh" reloads the page; the close button only hides the
 * toast, and the next page load asks again. A page with unsaved changes keeps its leave guard
 * (`beforeunload`), so the browser asks before the reload. The native apps' web view gets no
 * toast: the app opens a fresh page each time.
 *
 * This is the one poll of the app. It gives the new build to the What's new host
 * (`appUpdateStore`). For a newer version with published notes the host shows its update panel
 * instead of this toast, and the toast closes when the panel takes over.
 */
const AppUpdateNoticeComponent: React.FC<AppUpdateNoticeProps> = ({ fetchImpl = fetch, reload = reloadPage }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { show } = useShowToast();
  const toasts = useToasts();
  const newBuild = useVersionPoll(!isNativeEmbedPage(), fetchImpl);
  const owner = useUpdatePromptOwner(newBuild?.buildId ?? null);
  const shown = useRef(false);
  const onRefresh = useCallback(() => reload(), [reload]);
  useToastAction(APP_UPDATE_TOAST_ACTION, onRefresh);

  useEffect(() => {
    publishNewBuild(newBuild);
  }, [newBuild]);

  useEffect(() => {
    if (newBuild === null || owner !== 'plain' || shown.current) {
      return;
    }
    shown.current = true;
    show({
      type: ToastType.info,
      uniqueId: APP_UPDATE_TOAST_ID,
      autoHide: false,
      title: intl.formatMessage(appUpdateMessages.title),
      description: intl.formatMessage(appUpdateMessages.body),
      action: { label: intl.formatMessage(appUpdateMessages.refresh), actionId: APP_UPDATE_TOAST_ACTION },
    });
  }, [intl, show, newBuild, owner]);

  // The What's new panel took over: the plain toast goes, so only one prompt shows.
  useEffect(() => {
    if (owner !== 'panel') {
      return;
    }
    toasts
      .filter((toast) => toast.uniqueId === APP_UPDATE_TOAST_ID && !toast.isDismissed)
      .forEach((toast) => dispatch(closeToast({ toastId: toast.id })));
  }, [owner, toasts, dispatch]);

  return null;
};

export const AppUpdateNotice = TypedMemo(AppUpdateNoticeComponent);
